import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../utils/axiosInstance';
import config from '../utils/config';

const READ_EVENT = 'app-user-note-read';
const REFRESH_INTERVAL_MS = 60_000;

export function notifyNoteRead(): void {
  window.dispatchEvent(new Event(READ_EVENT));
}

type UnreadCount = { count: number | null; error: boolean };
const UnreadCountContext = createContext<UnreadCount>({ count: null, error: false });

export function UnreadNoteCountProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { id, isLoggedIn, hasPermission, scopes } = useAuth();
  const allowed = isLoggedIn && hasPermission('firestore_read') && scopes?.includes('/');
  const [count, setCount] = useState<number | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    let request = 0;
    if (!allowed) {
      setCount(null);
      setError(false);
      return;
    }
    const load = async () => {
      if (document.visibilityState === 'hidden') return;
      const current = ++request;
      try {
        const response = await axiosInstance.get<{ unreadNotes: number }>(
          `${config.firebaseApiUrl}/fb/homeowners/unread-count`,
        );
        if (!active || current !== request) return;
        setCount(response.data.unreadNotes);
        setError(false);
      } catch {
        if (!active || current !== request) return;
        setCount(null);
        setError(true);
      }
    };
    const onVisible = () => { if (document.visibilityState === 'visible') void load(); };
    const onRead = () => { void load(); };
    void load();
    const interval = window.setInterval(() => { void load(); }, REFRESH_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener(READ_EVENT, onRead);
    return () => {
      active = false;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener(READ_EVENT, onRead);
    };
  }, [allowed, id, pathname]);

  return <UnreadCountContext.Provider value={{ count, error }}>{children}</UnreadCountContext.Provider>;
}

export function useUnreadNoteCount(): UnreadCount {
  return useContext(UnreadCountContext);
}
